import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription, interval, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { FeedbackService } from 'src/app/services/feedback.service';
import { MessageServiceService } from 'src/app/services/message-service.service';
import { LatestFeedbackRequest, LatestFeedbackRequestApiResponse, ChatMessage, FeedAgentAcceptResponse } from 'src/app/models/feedback-notification.model';

const POLL_INTERVAL_MS = 5000;
const NOTIFICATION_SOUND_PATH = 'assets/audio/agent-notification.wav';
const DECLINE_SNOOZE_MS = 5000;

@Component({
  selector: 'app-feedback-notification',
  templateUrl: './feedback-notification.component.html',
  styleUrls: ['./feedback-notification.component.scss']
})
export class FeedbackNotificationComponent implements OnInit, OnDestroy {
  pendingCard: LatestFeedbackRequest | null = null;
  accepting = false;

  // Chat-related properties
  chatActive = false;
  chatSessionId = '';
  chatPhoneNumber = '';
  chatMessages: ChatMessage[] = [];
  chatIsExited = false;
  chatFeedbackId = 0;
  chatFeedbackType = 0;
  chatAgentId = 0;

  private fbTypeLabels: { [key: number]: string } = { 1: 'IP', 2: 'OP', 3: 'Emergency' };

  private lastShownKey: string | null = null;
  private declinedKey: string | null = null;
  private declinedExpiry = 0;
  private acceptedKey: string | null = null;
  private pollSubscription: Subscription | null = null;
  private audioUnlocked = false;
  private soundPending = false;
  private unlockHandler: (() => void) | null = null;
  private notificationAudio: HTMLAudioElement | null = null;

  constructor(
    private feedbackService: FeedbackService,
    private messageService: MessageServiceService
  ) {}

  ngOnInit(): void {
    this.initAudio();
    if (this.isAgentUser()) {
      console.log('Feedback notification polling started for agent user');
      this.startPolling();
      // Trigger initial poll immediately
      this.feedbackService.getLatestFeedBackRequestAgent().subscribe(
        (res) => this.handlePollResult(res),
        (err) => console.error('Initial feedback poll error:', err)
      );
    }
  }

  ngOnDestroy(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
    }
    // Restore body scroll in case chat modal was open
    if (this.chatActive) {
      document.body.style.overflow = 'auto';
    }
    // Note: Event listeners for audio unlock are kept for app lifetime
    // Remove specific listeners only if needed
    if (this.unlockHandler) {
      document.removeEventListener('click', this.unlockHandler);
      document.removeEventListener('keydown', this.unlockHandler);
      document.removeEventListener('touchstart', this.unlockHandler);
    }
  }

  private initAudio(): void {
    this.notificationAudio = new Audio(NOTIFICATION_SOUND_PATH);
    this.notificationAudio.preload = 'auto';
    this.notificationAudio.volume = 1;
    this.notificationAudio.load();

    const attemptUnlock = () => {
      if (this.audioUnlocked) {
        return;
      }
      
      this.audioUnlocked = true;
      console.log('Audio context unlocked');
      
      // If sound is pending, play it now
      if (this.soundPending) {
        this.soundPending = false;
        this.playNotificationSound();
        return;
      }

      // Attempt silent play to fully unlock audio context
      const audio = this.notificationAudio;
      if (audio) {
        audio.currentTime = 0;
        const originalVolume = audio.volume;
        audio.volume = 0; // Mute for unlock attempt
        
        const playPromise = audio.play();
        if (playPromise) {
          playPromise
            .then(() => {
              audio.pause();
              audio.currentTime = 0;
              audio.volume = originalVolume;
            })
            .catch((err) => {
              audio.volume = originalVolume;
              console.debug('Silent audio unlock failed (expected on some browsers):', err.message);
            });
        }
      }
    };

    // Add multiple event listeners for better coverage
    const events = ['click', 'keydown', 'touchstart', 'touchend', 'mousedown', 'keyup'];
    events.forEach(event => {
      document.addEventListener(event, attemptUnlock, { once: false });
    });
  }

  private isAgentUser(): boolean {
    const userInfo = localStorage.getItem('LOGGED_USER_INFO');
    const currentUser: any = userInfo ? JSON.parse(userInfo) : null;
    return !!(currentUser && currentUser.roleName && currentUser.roleName.toLowerCase().includes('agent'));
  }

  private getAgentId(): number {
    return +(localStorage.getItem('LOGGED_USER_CRMID') || 0);
  }
  private startPolling(): void {
    this.pollSubscription = interval(POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this.feedbackService.getLatestFeedBackRequestAgent().pipe(
            catchError(err => {
              console.error('Error polling latest feedback request:', err);
              return of(null);
            })
          )
        )
      )
      .subscribe((res) => this.handlePollResult(res));
  }

  private handlePollResult(res: LatestFeedbackRequestApiResponse | null): void {
    // null means the poll request itself failed — don't wipe an existing card over a network blip.
    if (!res) {
      return;
    }

    if (this.declinedExpiry && Date.now() >= this.declinedExpiry) {
      this.declinedKey = null;
      this.declinedExpiry = 0;
      this.lastShownKey = null; // Reset to allow notification to be shown again with sound
    }

    const record = res.isSuccess ? res.response : null;
    if (!record || !record.mobileNumber) {
      this.pendingCard = null;
      this.acceptedKey = null;
      return;
    }

    const feedbackType = this.resolveFeedbackType(record);
    const normalizedRecord: LatestFeedbackRequest = {
      ...record,
      feedbackType
    };

    const key = `${normalizedRecord.mobileNumber}_${normalizedRecord.feedbackType}`;

    if (key === this.acceptedKey || key === this.declinedKey) {
      this.pendingCard = null;
      return;
    }

    if (key !== this.lastShownKey) {
      this.lastShownKey = key;
      this.playNotificationSound();
    }
    this.pendingCard = normalizedRecord;
  }

  private resolveFeedbackType(record: any): number {
    const value = record.feedbackType != null ? record.feedbackType :
      (record.fbType != null ? record.fbType : record.FbType);
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private playNotificationSound(): void {
    if (!this.notificationAudio) {
      this.soundPending = true;
      return;
    }

    if (!this.audioUnlocked) {
      this.soundPending = true;
      return;
    }

    const audio = this.notificationAudio;
    try {
      // Ensure audio is stopped
      audio.pause();
      // Reset to beginning
      audio.currentTime = 0;
      // Small delay to ensure audio element is ready
      setTimeout(() => {
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('Feedback notification sound played successfully');
            })
            .catch(err => {
              console.error('Unable to play feedback notification sound:', err);
              // Try again with audio unlock
              if (!this.audioUnlocked) {
                this.soundPending = true;
              }
              // Reset for next attempt
              audio.currentTime = 0;
            });
        }
      }, 100);
    } catch (err) {
      console.error('Error in playNotificationSound:', err);
      this.soundPending = true;
    }
  }

  fbTypeLabel(fbType: number): string {
    return this.fbTypeLabels[fbType] || '-';
  }

  accept(card: LatestFeedbackRequest): void {
    if (this.accepting) { return; }
    this.accepting = true;
    const feedbackType = this.resolveFeedbackType(card);
    const key = `${card.mobileNumber}_${feedbackType}`;

    this.feedbackService.feedAgentAccept({
      phoneNumber: card.mobileNumber,
      agentId: this.getAgentId(),
      feedbackType,
      feedbackId: card.feedbackId
    }).subscribe(
      (response: FeedAgentAcceptResponse) => {
        this.accepting = false;
        this.acceptedKey = key;
        this.pendingCard = null;
        
        // Store chat data and open chat modal
        if (response.statusCode === 200 && response.success) {
          this.chatSessionId = response.session_id;
          this.chatPhoneNumber = card.mobileNumber;
          this.chatMessages = response.chatHistory || [];
          this.chatFeedbackId = card.feedbackId;
          const fbType = feedbackType || 0;
          this.chatFeedbackType = fbType;
          this.chatAgentId = this.getAgentId();
          this.chatIsExited = false;
          console.log('Accept - card raw:', JSON.stringify(card));
          console.log('Accept - chatFeedbackType:', this.chatFeedbackType, 'chatAgentId:', this.chatAgentId);
          this.chatActive = true;
          document.body.style.overflow = 'hidden';
        } else {
          this.messageService.showErrorMessage(response.message || 'Unable to accept feedback request.');
        }
      },
      () => {
        this.accepting = false;
        this.messageService.showErrorMessage('Unable to accept feedback request.');
      }
    );
  }

  onChatExit(): void {
    this.chatActive = false;
    this.chatSessionId = '';
    this.chatPhoneNumber = '';
    this.chatMessages = [];
    this.chatIsExited = false;
    // Restore body scroll
    document.body.style.overflow = 'auto';
  }

  onChatExited(): void {
    // Agent clicked Exit Chat — close the popup; agent can reopen from the FeedBack Request table
    this.onChatExit();
  }

  decline(card: LatestFeedbackRequest): void {
    this.declinedKey = `${card.mobileNumber}_${this.resolveFeedbackType(card)}`;
    this.declinedExpiry = Date.now() + DECLINE_SNOOZE_MS;
    this.pendingCard = null;
  }
}
